import { useEffect, useRef, useState } from 'react';
import { resolveSnsDomainForAddress } from '../utils/telegramAuth';

const snsCache: Map<string, { domain: string | null; timestamp: number }> = new Map();
const CACHE_DURATION = 5 * 60 * 1000;
const FAILED_LOOKUP_CACHE_DURATION = 60 * 1000;
const ongoingFetches: Map<string, Promise<string | null>> = new Map();

export function useSNSName(
  address: string | undefined,
  enabled: boolean = true,
  refreshToken: number = 0
): {
  snsName: string | null;
  isLoading: boolean;
  error: string | null;
} {
  const [snsName, setSnsName] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isFetchingRef = useRef(false);

  useEffect(() => {
    if (!address || !enabled) {
      setSnsName(null);
      setIsLoading(false);
      setError(null);
      return;
    }

    const cacheKey = address;
    const cached = snsCache.get(cacheKey);
    if (cached) {
      const cacheAge = Date.now() - cached.timestamp;
      const cacheDuration = cached.domain ? CACHE_DURATION : FAILED_LOOKUP_CACHE_DURATION;
      if (cacheAge < cacheDuration) {
        setSnsName(cached.domain);
        setIsLoading(false);
        setError(null);
        return;
      }
    }

    if (isFetchingRef.current) {
      return;
    }

    const fetchName = async () => {
      const existingFetch = ongoingFetches.get(cacheKey);
      if (existingFetch) {
        try {
          const domain = await existingFetch;
          setSnsName(domain);
          setIsLoading(false);
          return;
        } catch {
          // Continue with new fetch
        }
      }

      isFetchingRef.current = true;
      setIsLoading(true);
      setError(null);

      const fetchPromise = (async () => {
        try {
          const domain = await resolveSnsDomainForAddress(address);
          snsCache.set(cacheKey, { domain, timestamp: Date.now() });
          return domain;
        } catch (err: any) {
          snsCache.set(cacheKey, { domain: null, timestamp: Date.now() });
          setError(err instanceof Error ? err.message : 'Unknown error');
          return null;
        } finally {
          ongoingFetches.delete(cacheKey);
          isFetchingRef.current = false;
        }
      })();

      ongoingFetches.set(cacheKey, fetchPromise);
      const domain = await fetchPromise;
      setSnsName(domain);
      setIsLoading(false);
    };

    fetchName();
  }, [address, enabled, refreshToken]);

  return { snsName, isLoading, error };
}

